import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button, Grid, TextField, FormControlLabel, Switch, FormHelperText, Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { debounce } from "lodash";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone";
import SignaturePadComponent from "utils/SignaturePadComponent";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";

import { useDOUploadFile } from "hooks";
import { useAuth } from "hooks";

import { getDateTimeAuto } from "utils/dateFormats";
import { useLazySearchLoadingsWithDetailsQuery } from "store/services/shipmentLoadingApi";

const timestamp = new Date()
  .toISOString()
  .replace(/[-:]/g, "")
  .replace("T", "_")
  .replace(/\..+/, "");

const INITIAL_STATE = {
  account: null,
  entity: null,
  loadingId: null,
  shipmentId: [],
  unloadingTime: getDateTimeAuto(),
  attachments: [],
  documentUrl: "",
  signature: "",
  receiveBy: "",
  receiverPhone: "",
  responsible: "",
  isActive: true,
  receiveInBehalf: false,
};

const FreeUnLoadingForm = ({
  isBusy = null,
  freeUnLoadingDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();
  const { handleUploadFiles } = useDOUploadFile();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const signaturePadRef = useRef(null);
  const [signatureError, setSignatureError] = useState(false);

  // Local state for options
  const [loadingOptions, setLoadingOptions] = useState([]);

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted },
    reset,
    trigger,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity?._id ? viewEntity.entity : null,
      account: viewEntity?.entity?.account?._id ? viewEntity.entity.account : null,
      loadingId: state?.loading || null,
      shipmentId: state?.shipments || [],
    },
  });

  // Watch form fields
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchLoadingId = useWatch({ control, name: "loadingId" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchReceiveInBehalf = useWatch({ control, name: "receiveInBehalf" });

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();
  const [searchLoadings, { isLoading: loadingSearchResults }] = useLazySearchLoadingsWithDetailsQuery();
  const [fetchUsers, { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers }] = useLazyGetDDLSystemUserListQuery();

  // Mapped users for responsible field
  const _mappedUsers = useMemo(
    () =>
      userList?.map((el) => ({
        label: el?.name,
        id: el?._id,
      })) || [],
    [userList]
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!_watchAccount?._id || !_watchEntity?._id) {
        toast.error("Please select account and entity first");
        return;
      }

      try {
        const result = await searchLoadings({
          account: _watchAccount._id,
          entity: _watchEntity._id,
          query: query,
          limit: 50,
        }).unwrap();

        setLoadingOptions(result?.data || []);
      } catch (error) {
        setLoadingOptions([]);
      }
    }, 500),
    [_watchAccount?._id, _watchEntity?._id, searchLoadings]
  );

  // Load initial loadings (only when needed)
  const loadInitialLoadings = useCallback(async () => {
    if (!_watchAccount?._id || !_watchEntity?._id) return;

    try {
      const result = await searchLoadings({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        query: "",
        limit: 20, // Only load 20 initially for faster load
      }).unwrap();

      setLoadingOptions(result?.data || []);
    } catch (error) {
      setLoadingOptions([]);
    }
  }, [_watchAccount?._id, _watchEntity?._id, searchLoadings]);

  // Handle loading selection
  const handleLoadingSelection = useCallback((selectedLoading) => {
    if (!selectedLoading) {
      setValue("loadingId", null);
      setValue("shipmentId", []);
      return;
    }

    setValue("loadingId", selectedLoading);

    // Auto-select all available shipments
    const availableShipments = (selectedLoading.shipmentId || []).filter(
      (shipment) =>
        (shipment.status === "Loaded" || shipment.status === "Return") &&
        shipment.isOnHold !== true &&
        shipment.isConsolidated === true
    );

    setValue("shipmentId", availableShipments, { shouldValidate: true });
  }, [setValue]);

  // Available shipments from selected loading only
  const availableShipments = useMemo(() => {
    if (!_watchLoadingId?.shipmentId) return [];

    return _watchLoadingId.shipmentId.filter(
      (shipment) =>
        (shipment.status === "Loaded" || shipment.status === "Return") &&
        shipment.isOnHold !== true &&
        shipment.isConsolidated === true
    );
  }, [_watchLoadingId]);

  // Load initial loadings only once when entity is set
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id && !freeUnLoadingDetail && !state?.loading) {
      loadInitialLoadings();
    }
  }, [_watchAccount?._id, _watchEntity?._id, freeUnLoadingDetail, state?.loading, loadInitialLoadings]);

  // Auto-populate from state
  useEffect(() => {
    if (state?.loading) {
      setValue("loadingId", state.loading);
      if (state.loading.shipmentId) {
        const loadedShipments = state.loading.shipmentId.filter(
          (shipment) =>
            (shipment.status === "Loaded" || shipment.status === "Return") &&
            shipment.isOnHold !== true &&
            shipment.isConsolidated === true
        );
        setValue("shipmentId", loadedShipments);
      }
    }

    if (state?.shipments?.length) {
      setValue("shipmentId", state.shipments);
    }
  }, [state, setValue]);

  // Fetch entities when account changes
  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      if (_watchAccount?._id) {
        fetchEntities({ account: _watchAccount._id });
      }
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch users when entity changes
  useEffect(() => {
    if (_watchEntity?._id && !freeUnLoadingDetail) {
      fetchUsers({ entity: _watchEntity._id });
    }
  }, [_watchEntity?._id, fetchUsers, freeUnLoadingDetail]);

  // Reset loadings when entity changes
  useEffect(() => {
    if (_watchEntity?._id) {
      setLoadingOptions([]);
      setValue("loadingId", null);
      setValue("shipmentId", []);
    }
  }, [_watchEntity?._id, setValue]);

  // Reset form when editing
  useEffect(() => {
    if (!freeUnLoadingDetail) return;

    reset({
      ...freeUnLoadingDetail,
      unloadingTime: freeUnLoadingDetail?.unloadingTime
        ? new Date(freeUnLoadingDetail.unloadingTime).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      consolidationId: freeUnLoadingDetail?.consolidationId || null,
      collectionId: freeUnLoadingDetail?.collectionId || null,
      shipmentId: freeUnLoadingDetail?.shipmentId?.map((item) => item.shipment) || [],
      account: freeUnLoadingDetail?.account?._id ? freeUnLoadingDetail.account : null,
      entity: freeUnLoadingDetail?.entity?._id ? freeUnLoadingDetail.entity : null,
      loadingId: freeUnLoadingDetail?.loadingId?._id ? freeUnLoadingDetail.loadingId : null,
    });

    const accountId = freeUnLoadingDetail?.account?._id || viewEntity?.entity?.account?._id || null;
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }

    const entityId = freeUnLoadingDetail?.entity?._id || viewEntity?.entity?._id || null;
    if (entityId) {
      fetchUsers({ entity: entityId });
    }
  }, [reset, freeUnLoadingDetail, fetchEntities, fetchUsers, isAccountAdmin, isSystemAdmin, viewEntity?.entity]);

  // Set default responsible user
  useEffect(() => {
    if (loggedInUser && !freeUnLoadingDetail) {
      const responsibleUser = {
        label: loggedInUser.name,
        id: loggedInUser._id,
      };
      setValue("responsible", responsibleUser, { shouldValidate: true });
    }
  }, [loggedInUser, setValue, freeUnLoadingDetail]);

  // Convert data URL to file for signature
  const convertDataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Submit handler
  const handleOnShipmentUnLoadingSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const signatureDataUrl = signaturePadRef?.current?.getSignature();
      if (!signatureDataUrl) {
        setSignatureError(true);
        setIsSubmitting(false);
        return;
      }
      setSignatureError(false);

      const signatureFile = convertDataUrlToFile(
        signatureDataUrl,
        `unloading_signature_${timestamp}.png`
      );

      const shipmentId = data.shipmentId.map((shipment) => ({
        shipment: shipment._id,
        status: "Unloaded",
      }));

      // Separate existing and new attachments
      const [existingAttachments, newAttachments] = data?.attachments?.reduce(
        ([pass, fail], el) =>
          el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
        [[], []]
      );

      // Upload files in parallel
      const [signatureUpload, attachmentsUpload] = await Promise.all([
        handleUploadFiles([signatureFile], {
          folderName: "DO_FOLDER_NAME.UNLOADING_SIGNATURE",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        }),
        ...(newAttachments?.length
          ? [
            handleUploadFiles(newAttachments, {
              folderName: "SHIPMENT_UNLOADING",
              entity: viewEntity?.entity?._id,
              account: viewEntity?.entity?.account?._id,
            }),
          ]
          : [Promise.resolve({ uploadedFiles: [] })]),
      ]);

      const _shipmentUnLoadingPayload = {
        ...data,
        entity: data?.entity?._id || null,
        account: data?.account?._id || null,
        loadingId: data?.loadingId?._id || null,
        shipmentId,
        unloadingStatus:
          data.shipmentId.length < (data.loadingId?.shipmentId?.length || 0)
            ? "Partial"
            : "Complete",
        signature: signatureUpload.uploadedFiles[0]?.url || "",
        attachments: [
          ...existingAttachments,
          ...(attachmentsUpload?.uploadedFiles || []),
        ].map((attachment) => ({
          name: attachment.name || "",
          type: attachment.type || "",
          size: attachment.size || "",
          publicId: attachment.publicId || "",
          url: attachment.secureUrl || attachment.url || "",
        })),
        responsible: data?.responsible?.id || null,
      };

      await onSubmit(_shipmentUnLoadingPayload);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.message || "Failed to submit form");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("loadingId", null);
                      setValue("shipmentId", []);
                      setLoadingOptions([]);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin || loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("loadingId", null);
                      setValue("shipmentId", []);
                      setLoadingOptions([]);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null || value === ""
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    disabled={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="loadingId"
              control={control}
              rules={{ required: "Please select ShipmentLoading ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Loading"
                  required
                  value={value ?? null}
                  onInputChange={(_, newInputValue) => {
                    if (newInputValue.length >= 3) {
                      debouncedSearch(newInputValue);
                    } else if (newInputValue.length === 0) {
                      loadInitialLoadings();
                    }
                  }}
                  onChange={(_, data) => {
                    onChange(data);
                    handleLoadingSelection(data);
                  }}
                  getOptionLabel={(option) =>
                    option?.loadingId && option?.vehicle_id?.registration_number
                      ? `${option.loadingId} - ${option.vehicle_id.vehicle_id}(${option.vehicle_id?.registration_number})`
                      : option?.loadingId || ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id || value === null
                  }
                  options={loadingOptions}
                  loading={loadingSearchResults}
                  loadingText="Loading..."
                  helperText={
                    errors?.loadingId?.message ||
                    "Type 3+ characters to search or select from list"
                  }
                  error={!!errors?.loadingId}
                  disabled={!!state?.loading || !!freeUnLoadingDetail}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{
                validate: (value) =>
                  value.length > 0 || "Please select at least one Shipment ID.",
              }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple
                  label="Shipment"
                  required
                  value={value ?? []}
                  onChange={(_, data) => {
                    onChange(data);
                    trigger("shipmentId");
                  }}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.isReturning || option.status === "Return"
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={availableShipments}
                  helperText={
                    errors?.shipmentId?.message ||
                    "Select from loaded shipments"
                  }
                  error={!!errors?.shipmentId}
                  disabled={!_watchLoadingId || !!state?.shipments || !!freeUnLoadingDetail}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="responsible"
              control={control}
              rules={{ required: "Please select a Responsible User." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Responsible User"
                  loading={loadingUsers || fetchingUsers}
                  loadingText="Loading Responsible Users..."
                  options={_mappedUsers}
                  value={field.value || null}
                  onChange={(_, data) => {
                    field.onChange(data);
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  error={!!errors?.responsible}
                  helperText={errors?.responsible?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="unloadingTime"
              control={control}
              defaultValue={INITIAL_STATE.unloadingTime}
              rules={{ required: "Unloading Time is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unloading Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.unloadingTime}
                  helperText={errors?.unloadingTime?.message}
                />
              )}
            />
          </Grid>
          {_watchShipmentId?.length > 0 && (
            <Grid item sm={12}>
              <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Shipment Details
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Shipment</TableCell>
                      <TableCell>Sender</TableCell>
                      <TableCell>Receiver</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {_watchShipmentId.map((shipment, index) => {
                      const senderName = shipment?.customerId?.name
                        ? `CUS: ${shipment.customerId.name}`
                        : shipment?.vendor?.shopName
                          ? `VEN: ${shipment.vendor.shopName}`
                          : shipment?.vendor?.ownerName
                            ? `VEN: ${shipment.vendor.ownerName}`
                            : "Not Available";

                      const receiverName = shipment?.receiver?.name || "Not Available";

                      return (
                        <TableRow key={shipment?._id || index}>
                          <TableCell>{shipment?.shipmentId || "Unknown"}</TableCell>
                          <TableCell>{senderName}</TableCell>
                          <TableCell>{receiverName}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          <Grid item sm={6}>
            <div style={{ display: "flex", alignItems: "stretch", height: "100%" }}>
              <SignaturePadComponent
                label="Signature"
                ref={signaturePadRef}
                InputLabelProps={{ shrink: true }}
                width={640}
                height={260}
              />
            </div>
            {signatureError && (
              <FormHelperText error>Please sign before submitting.</FormHelperText>
            )}
          </Grid>
          <Grid item sm={6}>
            <FormHelperText>Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value || []}
                  onFileChange={(files) => {
                    setValue("attachments", files);
                    trigger("attachments");
                  }}
                  disabled={!!freeUnLoadingDetail}
                  acceptedFileTypes={{
                    "image/*": [".jpeg", ".jpg", ".png"],
                    "application/pdf": [".pdf"],
                  }}
                  error={!!errors.attachments}
                  helperText={errors.attachments?.message || ""}
                  showError={isSubmitted}
                />
              )}
            />
            <Grid item sm={5} mt={1}>
              <Controller
                name="receiveInBehalf"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (!e.target.checked) {
                            setValue("receiveBy", "");
                            setValue("receiverPhone", "");
                          }
                        }}
                      />
                    }
                    label="Receive In Behalf?"
                  />
                )}
              />
            </Grid>
          </Grid>

          {_watchReceiveInBehalf && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="receiveBy"
                  control={control}
                  rules={{ required: "Please provide receiver Name." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Receiver Name"
                      placeholder="Enter Receiver Name"
                      error={!!errors?.receiveBy}
                      helperText={errors?.receiveBy?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="receiverPhone"
                  control={control}
                  rules={{
                    required: "Receiver Phone is required",
                    validate: (value) => {
                      const digits = value.replace(/\D/g, "");
                      if (digits.length < 7 || digits.length > 17) {
                        return "Phone number must be between 7 and 17 digits";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Receiver Phone"
                      placeholder="Enter Receiver Phone"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          {freeUnLoadingDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnShipmentUnLoadingSubmit)}
          disabled={isBusy || isSubmitting}
        >
          {freeUnLoadingDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default FreeUnLoadingForm;